import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * A card is defined by its surface and a whisper of height, not by an outline.
 *
 * The border is `--border` — barely a shade off the card itself — and does no visual
 * work on its own; what actually separates the card from the page is the lighter fill
 * plus `shadow-sm`, two shadows so soft they read as the card sitting *on* the
 * background rather than being cut out of it.
 *
 * forwardRef: stat tiles wrap a Card in a Tooltip trigger, which needs the node.
 *
 * `min-w-0` is load-bearing, not tidiness. A card is almost always a grid or flex item, and
 * such an item defaults to `min-width: auto` — meaning it may refuse to shrink below the
 * min-content width of whatever it contains. On the reports page one card held a six-column
 * table whose min-content was 554px; at a 390px viewport that card claimed 556px, the grid
 * track grew to match, and *every sibling card on the page* was stretched to 556px with it —
 * so a chart card with nothing wide in it had its bars cut off by a table it never knew
 * about. Flooring the minimum at zero lets each card take the track it is given and hands
 * the overflow back to the element that should own it: the table's own `overflow-x-auto`
 * wrapper, which then scrolls sideways inside the card as it was written to.
 */
const Card = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(function Card(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground border-border/70 flex min-w-0 flex-col rounded-xl border shadow-sm',
        className,
      )}
      {...props}
    />
  );
});

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'flex items-start justify-between gap-3 px-5 pt-5 pb-4 [&+[data-slot=card-content]]:pt-0',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="card-title"
      className={cn('text-[15px] leading-tight font-semibold tracking-[-0.015em]', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-muted-soft mt-1 text-[13px] leading-relaxed', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-action" className={cn('flex items-center gap-2', className)} {...props} />;
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-5 py-5', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('border-border flex items-center gap-2 border-t px-5 py-3.5', className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardTitle, CardDescription, CardAction, CardContent, CardFooter };
